/**
 * Subscription billing page (active when billing_mode === "subscription").
 *
 * Shows the signed-in user's current tier + usage meter, the available paid
 * tiers, and a provider picker (PayPal vs LuniPay) to subscribe. In the default
 * one_time mode it points users to /pricing instead.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { googleSignIn } from "@/lib/auth";
import { Check, Loader2, ArrowRight, CheckCircle2, ExternalLink } from "lucide-react";

const fmtUsd = (n) => (typeof n === "number" ? `$${n.toFixed(2).replace(/\.00$/, "")}` : "—");

export default function Billing() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // public-catalog
  const [sub, setSub] = useState(null);
  const [quota, setQuota] = useState(null);
  const [busy, setBusy] = useState("");
  const [pickProvider, setPickProvider] = useState({}); // {tier_slug: 'paypal'|'lunipay'}
  const [lunipayOpened, setLunipayOpened] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, mysub, q] = await Promise.all([
        api.get("/billing/public-catalog"),
        api.get("/billing/my-subscription"),
        api.get("/billing/quota"),
      ]);
      setData(cat.data);
      setSub(mysub.data);
      setQuota(q.data?.authenticated ? q.data : null);
    } catch (e) {
      console.warn("billing load failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle PayPal subscription return.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("paypal_sub") === "success") {
      toast.success("Subscription approved! It activates within a moment.");
      window.history.replaceState({}, "", "/billing");
      setTimeout(load, 1500);
    } else if (params.get("paypal_sub") === "cancel") {
      toast.message("Subscription checkout cancelled.");
      window.history.replaceState({}, "", "/billing");
    }
  }, [load]);

  const origin = window.location.origin;

  const subscribe = async (tier, provider) => {
    if (!user) { googleSignIn(); return; }
    setBusy(`${tier.slug}:${provider}`);
    try {
      const { data: res } = await api.post("/billing/subscribe", {
        tier_slug: tier.slug, provider, origin_url: origin,
      });
      if (provider === "paypal") {
        if (res.approve_url) { window.location.assign(res.approve_url); return; }
        toast.error("Could not get PayPal approval link.");
      } else {
        // LuniPay — open checkout, then user claims.
        if (res.checkout_url) {
          window.open(res.checkout_url, "_blank", "noopener,noreferrer");
          setLunipayOpened(tier.slug);
          toast.message(`${processorName} opened in a new tab. Return and click 'I've paid' to activate.`);
        } else {
          toast.error(`This tier has no ${processorName} checkout link configured.`);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start subscription.");
    } finally {
      setBusy("");
    }
  };

  const claimLunipaySub = async (tier) => {
    setBusy(`${tier.slug}:claim`);
    try {
      const { data: res } = await api.post("/billing/lunipay/subscribe-claim", { tier_slug: tier.slug });
      toast.success(res.status === "already_active" ? "Already on this plan." : "Plan activated!");
      setLunipayOpened("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const cancel = async () => {
    setBusy("cancel");
    try {
      await api.post("/billing/cancel", {});
      toast.success("Subscription cancelled. You're on the free plan.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel.");
    } finally {
      setBusy("");
    }
  };

  const billingMode = data?.billing_mode;
  const providers = data?.providers || {};
  const paypalOn = !!providers.paypal_enabled;
  const lunipayOn = providers.lunipay_enabled !== false;
  const processorName = providers.processor_name || "Payment Processor";
  const currentTier = sub?.current_tier_slug || "free";

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[#1B1E22]" data-testid="billing-page">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-5 sm:px-8 py-12 sm:py-16">
        <p className="text-[11px] uppercase tracking-[0.18em] text-teal-700 dark:text-teal-400">Your plan</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
          Subscription & usage
        </h1>

        {loading ? (
          <div className="mt-10 flex items-center gap-2 text-slate-500 dark:text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading your plan…
          </div>
        ) : billingMode !== "subscription" ? (
          <div className="mt-8 rounded-xl border border-slate-200 dark:border-slate-800 p-6 bg-slate-50/60 dark:bg-slate-900/40" data-testid="billing-onetime-notice">
            <p className="text-sm text-slate-700 dark:text-slate-200">
              We're currently on one-off pricing — there's no subscription to manage. See the
              {" "}<Link to="/pricing" className="text-teal-700 dark:text-teal-400 underline underline-offset-2">Plans & Pricing</Link> page for the one-time options.
            </p>
          </div>
        ) : (
          <>
            {/* Current plan + usage meter */}
            {user ? (
              <div className="mt-8 rounded-xl border border-slate-200 dark:border-slate-800 p-6" data-testid="current-plan-card">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Current plan</p>
                    <p className="mt-1 font-display text-xl font-semibold text-slate-900 dark:text-slate-50">
                      {quota?.tier_name || currentTier}
                    </p>
                  </div>
                  {currentTier !== "free" && (
                    <button
                      onClick={cancel}
                      disabled={busy === "cancel"}
                      data-testid="cancel-subscription-btn"
                      className="h-9 px-4 rounded-md border border-slate-300 dark:border-slate-700 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
                    >
                      {busy === "cancel" ? "Cancelling…" : "Cancel plan"}
                    </button>
                  )}
                </div>
                {quota && (
                  <div className="mt-5" data-testid="usage-meter">
                    <div className="flex items-baseline justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-300">
                        {quota.used} of {quota.allowance} plan generations used this {quota.period}
                      </span>
                      <span className="text-slate-500 dark:text-slate-400 text-xs">{quota.remaining} left</span>
                    </div>
                    <div className="mt-2 h-2 w-full rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                      <div
                        className="h-full bg-teal-600 dark:bg-teal-500 transition-all"
                        style={{ width: `${Math.min(100, (quota.used / Math.max(1, quota.allowance)) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-8 rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50/70 dark:bg-amber-900/20 p-6">
                <p className="text-sm text-amber-900 dark:text-amber-200">
                  Sign in to view your plan and subscribe.
                </p>
                <button onClick={googleSignIn} data-testid="billing-signin-btn" className="mt-3 h-10 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500">
                  Sign in
                </button>
              </div>
            )}

            {/* Tier cards */}
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="tier-grid">
              {(data?.catalog || []).filter((t) => t.is_paid).map((tier) => {
                const isCurrent = tier.slug === currentTier;
                const prov = pickProvider[tier.slug] || (paypalOn ? "paypal" : "lunipay");
                return (
                  <div
                    key={tier.slug}
                    data-testid={`tier-card-${tier.slug}`}
                    className={`rounded-xl border p-6 flex flex-col ${
                      isCurrent ? "border-teal-500 dark:border-teal-500 bg-teal-50/40 dark:bg-teal-900/10" : "border-slate-200 dark:border-slate-800"
                    }`}
                  >
                    <p className="font-display text-lg font-semibold text-slate-900 dark:text-slate-50">{tier.display_name}</p>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{tier.description}</p>
                    <p className="mt-4 font-display text-3xl font-semibold text-slate-900 dark:text-slate-50">
                      {fmtUsd(tier.price)}
                      <span className="text-xs text-slate-500 dark:text-slate-400 font-normal ml-1">/ {tier.period}</span>
                    </p>
                    <ul className="mt-4 space-y-1.5 text-sm text-slate-700 dark:text-slate-200 flex-1">
                      <li className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-600" /> {tier.allowance} plan generations / {tier.period}</li>
                    </ul>

                    {isCurrent ? (
                      <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-teal-700 dark:text-teal-400">
                        <CheckCircle2 className="h-4 w-4" /> Your current plan
                      </div>
                    ) : (
                      <div className="mt-5 space-y-2">
                        {paypalOn && lunipayOn && (
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setPickProvider((p) => ({ ...p, [tier.slug]: "paypal" }))}
                              data-testid={`tier-${tier.slug}-pick-paypal`}
                              className={`h-9 rounded-md border text-xs font-medium ${prov === "paypal" ? "border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200" : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}
                            >PayPal / Card</button>
                            <button
                              type="button"
                              onClick={() => setPickProvider((p) => ({ ...p, [tier.slug]: "lunipay" }))}
                              data-testid={`tier-${tier.slug}-pick-lunipay`}
                              className={`h-9 rounded-md border text-xs font-medium ${prov === "lunipay" ? "border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200" : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300"}`}
                            >{processorName}</button>
                          </div>
                        )}
                        {prov === "lunipay" && lunipayOpened === tier.slug ? (
                          <button
                            onClick={() => claimLunipaySub(tier)}
                            disabled={busy.startsWith(tier.slug)}
                            data-testid={`tier-${tier.slug}-claim-btn`}
                            className="w-full h-10 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                          >
                            {busy.startsWith(tier.slug) ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                            I've paid — activate
                          </button>
                        ) : (
                          <button
                            onClick={() => subscribe(tier, prov)}
                            disabled={busy === `${tier.slug}:${prov}`}
                            data-testid={`tier-${tier.slug}-subscribe-btn`}
                            className="w-full h-10 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                          >
                            {busy === `${tier.slug}:${prov}` ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            {prov === "lunipay" ? <>Pay on {processorName} <ExternalLink className="h-4 w-4" /></> : <>Subscribe <ArrowRight className="h-4 w-4" /></>}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
